'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Star, Users, Send, Globe, Shield, ExternalLink, ChevronLeft, Wallet, ThumbsUp, ThumbsDown, CheckCircle, Clock, Calendar, DollarSign, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useWalletAuth } from '@/contexts/WalletAuthContext';

interface AirdropPhase {
  phase_number: number;
  phase_name: string;
  description: string;
  start_date: string;
  end_date: string;
  reward_amount: string;
  status: string;
}

interface AirdropConnection {
  id: string;
  wallet_address: string;
  telegram_handle: string;
  twitter_handle: string;
  discord_handle: string;
  connected_at: string;
}

interface AirdropVote {
  id: string;
  wallet_address: string;
  vote_type: string;
  rating: number;
}

interface AirdropDetail {
  id: string;
  title: string;
  project_name: string;
  description: string;
  reward_amount: string;
  token_symbol: string;
  category: string;
  status: string;
  image_url: string;
  official_link: string;
  join_url: string;
  telegram_link: string;
  blockchain: string;
  time_left: string;
  total_participants: string;
  participation_type: string;
  is_featured: boolean;
  start_date: string;
  end_date: string;
  daily_volume: number;
  holder_count: number;
  countdown_end_at: string;
  countdown_label: string;
  admin_rating: number;
  token_price: string;
  audit_report_url: string;
  audit_report_name: string;
  twitter_link: string;
  discord_link: string;
  instagram_link: string;
  youtube_link: string;
  github_link: string;
  reddit_link: string;
  medium_link: string;
  linkedin_link: string;
  website_link: string;
  connected_users_count: number;
  like_count: number;
}

interface AirdropDetailPageProps {
  airdropId: string;
  onBack: () => void;
}

function StarRating({ rating, onRate, readonly = false }: { rating: number; onRate?: (r: number) => void; readonly?: boolean }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onRate?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
        >
          <Star
            size={16}
            className={`transition-colors ${(hover || rating) >= star ? 'text-[#F59E0B] fill-[#F59E0B]' : 'text-[#4A5568]'}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function AirdropDetailPage({ airdropId, onBack }: AirdropDetailPageProps) {
  const { user } = useAuth();
  const { walletUser, walletAddress } = useWalletAuth();
  const [airdrop, setAirdrop] = useState<AirdropDetail | null>(null);
  const [phases, setPhases] = useState<AirdropPhase[]>([]);
  const [connections, setConnections] = useState<AirdropConnection[]>([]);
  const [votes, setVotes] = useState<AirdropVote[]>([]);
  const [loading, setLoading] = useState(true);
  const [userVote, setUserVote] = useState<AirdropVote | null>(null);
  const [userRating, setUserRating] = useState(0);
  const [voting, setVoting] = useState(false);
  const [connectModal, setConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({ telegram_handle: '', twitter_handle: '', discord_handle: '' });
  const [connecting, setConnecting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'holders' | 'phases' | 'social'>('overview');

  const effectiveWallet = walletAddress || walletUser?.walletAddress || '';

  useEffect(() => {
    const fetchData = async () => {
      const supabase = createClient();
      const [airdropRes, phRes, connRes, vtRes] = await Promise.all([
        supabase.from('airdrops').select('*').eq('id', airdropId).single(),
        supabase.from('airdrop_phases').select('*').eq('airdrop_id', airdropId).order('phase_number'),
        supabase.from('airdrop_connections').select('*').eq('airdrop_id', airdropId).order('connected_at', { ascending: false }),
        supabase.from('airdrop_votes').select('*').eq('airdrop_id', airdropId),
      ]);
      const dropData = airdropRes.data;
      const phData = phRes.data;
      const connData = connRes.data;
      const vtData = vtRes.data;
      setAirdrop(dropData as AirdropDetail);
      setPhases((phData as AirdropPhase[]) || []);
      setConnections((connData as AirdropConnection[]) || []);
      setVotes((vtData as AirdropVote[]) || []);
      if (effectiveWallet && vtData) {
        const myVote = (vtData as AirdropVote[]).find((v: AirdropVote) => v.wallet_address === effectiveWallet);
        if (myVote) { setUserVote(myVote); setUserRating(myVote.rating || 0); }
      }
      setLoading(false);
    };
    fetchData();
  }, [airdropId, effectiveWallet]);

  const avgRating = votes.length > 0
    ? votes.filter(v => v.rating > 0).reduce((sum, v) => sum + (v.rating || 0), 0) / Math.max(1, votes.filter(v => v.rating > 0).length)
    : 0;
  const upVotes = votes.filter(v => v.vote_type === 'up').length;
  const downVotes = votes.filter(v => v.vote_type === 'down').length;
  const displayRating = airdrop?.admin_rating ?? (avgRating > 0 ? avgRating : 0);

  const handleVote = async (voteType: 'up' | 'down', rating: number = 0) => {
    if (!effectiveWallet) { alert('Please connect your wallet to vote.'); return; }
    setVoting(true);
    const supabase = createClient();
    const payload = {
      airdrop_id: airdropId,
      wallet_address: effectiveWallet,
      user_id: user?.id || null,
      vote_type: voteType,
      rating: rating || userRating,
    };
    if (userVote) {
      await supabase.from('airdrop_votes').update(payload).eq('id', userVote.id);
    } else {
      await supabase.from('airdrop_votes').insert(payload);
    }
    // Refresh votes
    const { data: vt } = await supabase.from('airdrop_votes').select('*').eq('airdrop_id', airdropId);
    setVotes(vt || []);
    const myVote = (vt || []).find((v: AirdropVote) => v.wallet_address === effectiveWallet);
    if (myVote) setUserVote(myVote);
    setVoting(false);
  };

  const handleRating = async (r: number) => {
    setUserRating(r);
    if (!effectiveWallet) { alert('Please connect your wallet to rate.'); return; }
    setVoting(true);
    const supabase = createClient();
    const payload = {
      airdrop_id: airdropId,
      wallet_address: effectiveWallet,
      user_id: user?.id || null,
      vote_type: userVote?.vote_type || 'up',
      rating: r,
    };
    if (userVote) {
      await supabase.from('airdrop_votes').update(payload).eq('id', userVote.id);
    } else {
      await supabase.from('airdrop_votes').insert(payload);
    }
    const { data: vt } = await supabase.from('airdrop_votes').select('*').eq('airdrop_id', airdropId);
    setVotes(vt || []);
    const myVote = (vt || []).find((v: AirdropVote) => v.wallet_address === effectiveWallet);
    if (myVote) setUserVote(myVote);
    setVoting(false);
  };

  const handleConnect = async () => {
    if (!effectiveWallet) { alert('Please connect your wallet first.'); return; }
    setConnecting(true);
    const supabase = createClient();
    const existing = connections.find(c => c.wallet_address === effectiveWallet);
    if (existing) {
      await supabase.from('airdrop_connections').update({ ...connectForm }).eq('id', existing.id);
    } else {
      await supabase.from('airdrop_connections').insert({
        airdrop_id: airdropId,
        wallet_address: effectiveWallet,
        user_id: user?.id || null,
        ...connectForm,
      });
      // Increment connected_users_count
      await supabase.from('airdrops').update({ connected_users_count: (airdrop?.connected_users_count || 0) + 1 }).eq('id', airdropId);
    }
    const { data: conn } = await supabase.from('airdrop_connections').select('*').eq('airdrop_id', airdropId).order('connected_at', { ascending: false });
    setConnections(conn || []);
    setConnectModal(false);
    setConnecting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#07111F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#16C2FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!airdrop) {
    return (
      <div className="min-h-screen bg-[#07111F] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle size={40} className="text-[#93A4B8] mx-auto mb-4" />
          <p className="text-[#93A4B8]">Airdrop not found.</p>
          <button onClick={onBack} className="mt-4 text-[#16C2FF] hover:underline text-sm">← Back to Hub</button>
        </div>
      </div>
    );
  }

  const symbolChar = airdrop.token_symbol?.charAt(0) || airdrop.project_name?.charAt(0) || 'A';
  const socialLinks = [
    { label: 'Twitter/X', url: airdrop.twitter_link, icon: '𝕏' },
    { label: 'Telegram', url: airdrop.telegram_link, icon: '✈' },
    { label: 'Discord', url: airdrop.discord_link, icon: '💬' },
    { label: 'Instagram', url: airdrop.instagram_link, icon: '📸' },
    { label: 'YouTube', url: airdrop.youtube_link, icon: '▶' },
    { label: 'GitHub', url: airdrop.github_link, icon: '⌨' },
    { label: 'Reddit', url: airdrop.reddit_link, icon: '🔴' },
    { label: 'Medium', url: airdrop.medium_link, icon: 'M' },
    { label: 'LinkedIn', url: airdrop.linkedin_link, icon: 'in' },
    { label: 'Website', url: airdrop.website_link || airdrop.official_link, icon: '🌐' },
  ].filter(s => s.url);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'holders', label: `Holders & Connected (${connections.length})` },
    { id: 'phases', label: `Phases (${phases.length})` },
    { id: 'social', label: 'Social Links' },
  ];

  return (
    <div className="min-h-screen bg-[#07111F] pb-16">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[rgba(22,194,255,0.08)] px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#93A4B8] hover:text-[#16C2FF] transition-colors mb-4">
            <ChevronLeft size={16} /> Back to Airdrop Hub
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-sora font-700 text-2xl flex-shrink-0 bg-[rgba(22,194,255,0.1)] border border-[rgba(22,194,255,0.2)] text-[#16C2FF]">
              {symbolChar}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-sora font-700 text-2xl text-[#E6F1FF]">{airdrop.project_name}</h1>
                {airdrop.status === 'verified' && <CheckCircle size={18} className="text-[#00E5A8]" />}
                {airdrop.is_featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(245,158,11,0.15)] text-[#F59E0B]">Featured</span>}
              </div>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="font-mono text-sm text-[#93A4B8]">{airdrop.token_symbol}</span>
                {airdrop.blockchain && <span className="text-xs text-[#93A4B8] bg-[rgba(255,255,255,0.04)] px-2 py-0.5 rounded">{airdrop.blockchain}</span>}
                {airdrop.category && <span className="text-xs text-[#93A4B8] bg-[rgba(255,255,255,0.04)] px-2 py-0.5 rounded">{airdrop.category}</span>}
                {airdrop.token_price && (
                  <span className="flex items-center gap-1 text-xs text-[#00E5A8] bg-[rgba(0,229,168,0.08)] px-2 py-0.5 rounded">
                    <DollarSign size={10} /> {airdrop.token_price}
                  </span>
                )}
              </div>
              {/* Rating display */}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <StarRating rating={Math.round(displayRating)} readonly />
                  <span className="text-xs text-[#93A4B8]">
                    {displayRating > 0 ? displayRating.toFixed(1) : 'No rating'}
                    {airdrop.admin_rating ? ' (Admin)' : votes.filter(v => v.rating > 0).length > 0 ? ` (${votes.filter(v => v.rating > 0).length} votes)` : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#93A4B8]">
                  <span className="flex items-center gap-1"><ThumbsUp size={11} className="text-[#00E5A8]" /> {upVotes}</span>
                  <span className="flex items-center gap-1"><ThumbsDown size={11} className="text-red-400" /> {downVotes}</span>
                </div>
                {airdrop.audit_report_url && (
                  <a href={airdrop.audit_report_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-[#00E5A8] bg-[rgba(0,229,168,0.08)] px-2 py-0.5 rounded hover:bg-[rgba(0,229,168,0.15)] transition-colors">
                    <Shield size={10} /> Audit Report
                  </a>
                )}
              </div>
            </div>
            {/* Vote & Connect buttons */}
            <div className="flex flex-col gap-2 flex-shrink-0">
              {airdrop.join_url || airdrop.official_link ? (
                <a href={airdrop.join_url || airdrop.official_link} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-[#16C2FF] text-[#07111F] rounded-lg text-sm font-semibold hover:bg-[#0EA5E9] transition-colors">
                  Join Airdrop <ExternalLink size={13} />
                </a>
              ) : null}
              <button onClick={() => setConnectModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[rgba(22,194,255,0.1)] text-[#16C2FF] border border-[rgba(22,194,255,0.2)] rounded-lg text-sm font-medium hover:bg-[rgba(22,194,255,0.15)] transition-colors">
                <Wallet size={13} /> Connect Wallet
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-6">
        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-3 text-center">
            <div className="text-xs text-[#93A4B8] mb-1">Reward</div>
            <div className="font-sora font-700 text-sm text-[#00E5A8]">{airdrop.reward_amount || '—'}</div>
          </div>
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-3 text-center">
            <div className="text-xs text-[#93A4B8] mb-1">Daily Volume</div>
            <div className="font-mono text-sm text-[#16C2FF]">${((airdrop.daily_volume || 0) / 1000).toFixed(0)}K</div>
          </div>
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-3 text-center">
            <div className="text-xs text-[#93A4B8] mb-1">Holders</div>
            <div className="font-mono text-sm text-[#00E5A8]">{(airdrop.holder_count || 0).toLocaleString()}</div>
          </div>
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-3 text-center">
            <div className="text-xs text-[#93A4B8] mb-1">Connected</div>
            <div className="font-mono text-sm text-[#A78BFA]">{airdrop.connected_users_count || connections.length}</div>
          </div>
        </div>

        {/* Wallet Voting Section */}
        <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-sm font-semibold text-[#E6F1FF] mb-1">Rate & Vote This Airdrop</h3>
              <p className="text-xs text-[#93A4B8]">Connect your wallet to vote and rate. Your vote is tied to your wallet address.</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] text-[#93A4B8] uppercase tracking-wider">Your Rating</span>
                <StarRating rating={userRating} onRate={handleRating} />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleVote('up')}
                  disabled={voting}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    userVote?.vote_type === 'up' ?'bg-[rgba(0,229,168,0.2)] text-[#00E5A8] border border-[rgba(0,229,168,0.4)]' :'bg-[rgba(0,229,168,0.08)] text-[#00E5A8] border border-[rgba(0,229,168,0.15)] hover:bg-[rgba(0,229,168,0.15)]'
                  }`}
                >
                  <ThumbsUp size={12} /> {upVotes}
                </button>
                <button
                  onClick={() => handleVote('down')}
                  disabled={voting}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    userVote?.vote_type === 'down'
                      ? 'bg-[rgba(239,68,68,0.2)] text-red-400 border border-[rgba(239,68,68,0.4)]' :'bg-[rgba(239,68,68,0.08)] text-red-400 border border-[rgba(239,68,68,0.15)] hover:bg-[rgba(239,68,68,0.15)]'
                  }`}
                >
                  <ThumbsDown size={12} /> {downVotes}
                </button>
              </div>
            </div>
          </div>
          {!effectiveWallet && (
            <div className="mt-3 flex items-center gap-2 text-xs text-[#F59E0B] bg-[rgba(245,158,11,0.08)] px-3 py-2 rounded-lg">
              <Wallet size={12} /> Connect your wallet above to vote and rate this airdrop
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 flex-wrap mb-6 border-b border-[rgba(22,194,255,0.08)] pb-3">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[rgba(22,194,255,0.15)] text-[#16C2FF] border border-[rgba(22,194,255,0.3)]'
                  : 'text-[#93A4B8] hover:text-[#E6F1FF] border border-transparent'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[#E6F1FF] mb-3">About</h3>
              <p className="text-sm text-[#93A4B8] leading-relaxed">{airdrop.description || 'No description available.'}</p>
            </div>
            {airdrop.start_date && (
              <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#E6F1FF] mb-3">Timeline</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-[#93A4B8] mb-1 flex items-center gap-1"><Calendar size={11} /> Start Date</div>
                    <div className="text-sm text-[#E6F1FF]">{new Date(airdrop.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                  </div>
                  {airdrop.end_date && (
                    <div>
                      <div className="text-xs text-[#93A4B8] mb-1 flex items-center gap-1"><Clock size={11} /> End Date</div>
                      <div className="text-sm text-[#E6F1FF]">{new Date(airdrop.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'holders' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#E6F1FF]">Connected Wallets & Handles</h3>
              <span className="text-xs text-[#93A4B8]">{connections.length} connected</span>
            </div>
            {connections.length === 0 ? (
              <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-8 text-center">
                <Users size={32} className="text-[#4A5568] mx-auto mb-3" />
                <p className="text-sm text-[#93A4B8]">No wallets connected yet. Be the first!</p>
                <button onClick={() => setConnectModal(true)} className="mt-3 text-xs text-[#16C2FF] hover:underline">Connect your wallet</button>
              </div>
            ) : (
              <div className="space-y-2">
                {connections.map((conn, i) => (
                  <div key={conn.id} className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-4 flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-[rgba(22,194,255,0.1)] flex items-center justify-center text-xs font-mono text-[#16C2FF] flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-[#E6F1FF] truncate">{conn.wallet_address.slice(0, 8)}...{conn.wallet_address.slice(-6)}</div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {conn.telegram_handle && (
                          <span className="flex items-center gap-1 text-[10px] text-[#16C2FF]">
                            <Send size={9} /> {conn.telegram_handle}
                          </span>
                        )}
                        {conn.twitter_handle && (
                          <span className="flex items-center gap-1 text-[10px] text-[#93A4B8]">
                            𝕏 {conn.twitter_handle}
                          </span>
                        )}
                        {conn.discord_handle && (
                          <span className="flex items-center gap-1 text-[10px] text-[#93A4B8]">
                            💬 {conn.discord_handle}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-[10px] text-[#4A5568] flex-shrink-0">
                      {new Date(conn.connected_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'phases' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[#E6F1FF]">Airdrop Phases</h3>
            {phases.length === 0 ? (
              <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-8 text-center">
                <p className="text-sm text-[#93A4B8]">No phases declared for this airdrop.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {phases.map((phase) => (
                  <div key={phase.phase_number} className="bg-[#0D1B2A] border border-[rgba(139,92,246,0.15)] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[rgba(139,92,246,0.2)] text-[#A78BFA] text-xs font-bold flex items-center justify-center">{phase.phase_number}</span>
                        <span className="font-semibold text-sm text-[#E6F1FF]">{phase.phase_name}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        phase.status === 'active' ? 'bg-[rgba(0,229,168,0.1)] text-[#00E5A8]' :
                        phase.status === 'ended' ? 'bg-[rgba(147,164,184,0.1)] text-[#93A4B8]' :
                        'bg-[rgba(245,158,11,0.1)] text-[#F59E0B]'
                      }`}>{phase.status}</span>
                    </div>
                    {phase.description && <p className="text-xs text-[#93A4B8] mb-2">{phase.description}</p>}
                    <div className="flex items-center gap-4 flex-wrap text-xs text-[#93A4B8]">
                      {phase.reward_amount && <span className="text-[#00E5A8]">Reward: {phase.reward_amount}</span>}
                      {phase.start_date && <span>Start: {new Date(phase.start_date).toLocaleDateString()}</span>}
                      {phase.end_date && <span>End: {new Date(phase.end_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'social' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[#E6F1FF]">Social & Community Links</h3>
            {socialLinks.length === 0 ? (
              <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-8 text-center">
                <Globe size={32} className="text-[#4A5568] mx-auto mb-3" />
                <p className="text-sm text-[#93A4B8]">No social links available.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {socialLinks.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.08)] rounded-xl p-4 flex items-center gap-3 hover:border-[rgba(22,194,255,0.25)] transition-all group">
                    <span className="text-lg">{link.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-[#E6F1FF] group-hover:text-[#16C2FF] transition-colors">{link.label}</div>
                      <div className="text-[10px] text-[#4A5568] truncate">{link.url}</div>
                    </div>
                    <ExternalLink size={11} className="text-[#4A5568] group-hover:text-[#16C2FF] transition-colors flex-shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Connect Wallet Modal */}
      {connectModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.15)] rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-[rgba(22,194,255,0.1)]">
              <h2 className="text-base font-semibold text-[#E6F1FF]">Connect to This Airdrop</h2>
              <button onClick={() => setConnectModal(false)} className="text-[#93A4B8] hover:text-[#E6F1FF]">✕</button>
            </div>
            <div className="p-5 space-y-4">
              {effectiveWallet ? (
                <>
                  <div className="bg-[rgba(0,229,168,0.06)] border border-[rgba(0,229,168,0.15)] rounded-lg px-3 py-2">
                    <div className="text-xs text-[#93A4B8] mb-0.5">Connected Wallet</div>
                    <div className="font-mono text-xs text-[#00E5A8]">{effectiveWallet.slice(0, 12)}...{effectiveWallet.slice(-8)}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-[#93A4B8] mb-1">Telegram Handle (optional)</label>
                    <input value={connectForm.telegram_handle} onChange={e => setConnectForm(p => ({ ...p, telegram_handle: e.target.value }))} placeholder="@yourhandle" className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-3 py-2 text-sm text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)]" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#93A4B8] mb-1">Twitter/X Handle (optional)</label>
                    <input value={connectForm.twitter_handle} onChange={e => setConnectForm(p => ({ ...p, twitter_handle: e.target.value }))} placeholder="@yourtwitter" className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-3 py-2 text-sm text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)]" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#93A4B8] mb-1">Discord Handle (optional)</label>
                    <input value={connectForm.discord_handle} onChange={e => setConnectForm(p => ({ ...p, discord_handle: e.target.value }))} placeholder="username#0000" className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(22,194,255,0.1)] rounded-lg px-3 py-2 text-sm text-[#E6F1FF] focus:outline-none focus:border-[rgba(22,194,255,0.4)]" />
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <Wallet size={32} className="text-[#93A4B8] mx-auto mb-3" />
                  <p className="text-sm text-[#93A4B8]">Please connect your wallet first using the wallet button in the header.</p>
                </div>
              )}
            </div>
            {effectiveWallet && (
              <div className="flex items-center justify-end gap-3 p-5 border-t border-[rgba(22,194,255,0.1)]">
                <button onClick={() => setConnectModal(false)} className="px-4 py-2 text-sm text-[#93A4B8] hover:text-[#E6F1FF] transition-colors">Cancel</button>
                <button onClick={handleConnect} disabled={connecting}
                  className="flex items-center gap-2 px-4 py-2 bg-[#16C2FF] text-[#07111F] rounded-lg text-sm font-semibold hover:bg-[#0EA5E9] transition-colors disabled:opacity-50">
                  {connecting ? <div className="w-4 h-4 border-2 border-[#07111F] border-t-transparent rounded-full animate-spin" /> : <CheckCircle size={14} />}
                  Connect
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
